import * as logger from 'OK/logger';
import * as capture from 'OK/capture';
import ReactResourceResolver, {
    type AssetsResources,
    type AppModule,
    LoadingLibrariesError,
    LoadingDependenciesError,
} from 'OK/react/ReactResourceResolver';
import * as tracerConfiguration from 'OK/pms!tracerConfiguration';

import type { NodeElement } from '@ok/interfaces/core/react';
import type { ITracerConfiguration } from '@ok/interfaces/tracer/ITracerConfiguration';

const { logHTMLErrorsComponentList } = tracerConfiguration as ITracerConfiguration;

const EVENT_PREFIX = 'app-block';

/**
 * todo: игнорируем ошибки React 18: 418, 423, 424, 425. Фиксить будем постепенно в будущем:
 * 418 - https://jira.odkl.ru/browse/OKFRONT-753
 * 423 - https://jira.odkl.ru/browse/OKFRONT-754
 * 424 - https://jira.odkl.ru/browse/OKFRONT-755
 * 425 - https://jira.odkl.ru/browse/OKFRONT-756
 */
const IGNORED_ERRORS_REGEXP = /.*#(418|423|424|425).*/;

export class ReactCustomError extends Error {
    constructor({ message, stack }: { message: string, stack: string }) {
        super(message);
        this.stack = stack;
    }
}

/**
 * Логирование основных событий жизненного цикла Реакт-приложения.
 *
 * В логах приложения ошибки можно посмотреть в Graylog (подставь нужное значение ошибки!)
 * @see https://graylog.service.local.odkl.ru/search?q=facility%3A+odnoklassniki-web+AND+%22react-first-render-error%22
 * @see https://charts.odkl.ru/reports?report=936&tablename=WebGwtOperations&id_repository=1368167
 */
const enum LogEventCode {
    /**
     * Попытка инициализировать приложение.
     */
    INIT_ATTEMPT = 'react-init-try',
    /**
     * Попытка отрисовать приложение.
     */
    RENDER_ATTEMPT = 'react-render-try',
    /**
     * Приложение успешно отрисовалось.
     */
    RENDER_SUCCESS = 'react-render-success',
    /**
     * Произошла ошибка при загрузке базовых (@see RUNTIME_MODULES) модулей приложения.
     */
    LOADING_LIBRARIES_ERROR = 'react-loading-libs-error',
    /**
     * Произошла ошибка при загрузке ресурсов приложения
     */
    LOADING_DEPENDENCIES_ERROR = 'react-loading-dependencies-error',
    /**
     * Произошла общая ошибка при попытке инициализировать приложение.
     */
    RENDER_ERROR = 'react-loader-render-func-error',
    /**
     * Произошла ошибка при попытке отрисовать приложение.
     */
    FIRST_RENDER_ERROR = 'react-first-render-error',
}

const EVENTS = {
    INIT: EVENT_PREFIX + ':init',
    ACTIVATE: EVENT_PREFIX + ':activate',
    DEACTIVATE: EVENT_PREFIX + ':deactivate',
    ERROR: EVENT_PREFIX + ':error',
    REACT_ERROR: EVENT_PREFIX + ':react-error',
    LOG: EVENT_PREFIX + ':log',
    RENDER_COMPLETE: EVENT_PREFIX + ':render_complete',
    READY: EVENT_PREFIX + ':ready'
};

type ReactBlockEvent<T> = CustomEvent<T>;

export {AssetsResources};

/**
 * Отрисовывает и инициализирует Реакт-приложение.
 *
 * В базовом случае используй модуль `react-loader`.
 */
export default class ReactInitializer {
    private readonly element: NodeElement;
    private readonly requiredResources: AssetsResources;
    private readonly dataAttributes: Record<string, string>;
    private readonly uiParts: Record<string, string>;

    private readonly tagName: string;
    private appModule!: AppModule;
    private activationStartTime!: number;

    constructor(
        element: NodeElement,
        requiredResources: AssetsResources,
        dataAttributes: Record<string, string>,
        uiParts: Record<string, string>
    ) {
        this.element = element!;
        this.tagName = this.element.tagName.toLowerCase();

        this.requiredResources = requiredResources;
        this.dataAttributes = dataAttributes;
        this.uiParts = uiParts;
    }

    /**
     * Активирует Реакт-приложение на переданном элементе.
     */
    public initialize(): Promise<void | Error> {
        this.activationStartTime = now();

        this.logSuccess(LogEventCode.INIT_ATTEMPT);

        if (this.element && logHTMLErrorsComponentList.includes(this.tagName)) {
            this.element.serverHTML = this.element.innerHTML;
        }

        // Загружаем все необходимые для отрисовки и инициализации ресурсы Реакт-приложения.
        return ReactResourceResolver.resolveModule(this.requiredResources)
            .then(appModule => {
                this.appModule = appModule;

                this.logSuccess(LogEventCode.RENDER_ATTEMPT, simpleDurationValue(now() - this.activationStartTime));

                // Навешиваем обработчики перед отрисовкой.
                this.beforeRender();
                // Отрисовываем блок.
                this.render();

                this.logSuccess(LogEventCode.RENDER_SUCCESS, simpleDurationValue(now() - this.activationStartTime));
            })
            .catch(error => {
                if (error instanceof LoadingLibrariesError) {
                    this.logError(LogEventCode.LOADING_LIBRARIES_ERROR, error);
                } else if (error instanceof LoadingDependenciesError) {
                    this.logError(LogEventCode.LOADING_DEPENDENCIES_ERROR, error);
                } else {
                    this.logError(LogEventCode.RENDER_ERROR, new ReactCustomError({
                        message: error.message,
                        stack: error.stack
                    }));
                }

                throw error;
            })
    }

    /**
     * Деактивирует Реакт-приложение.
     */
    public destroy() {
        if (this.appModule && this.appModule.tags) {
            const appBlock = this.appModule.tags[this.tagName];
            if (appBlock) {
                appBlock.destroy(this.element!);
            }
        }

        if (this.element) {
            this.element.removeEventListener(EVENTS.INIT, this.onInitEventHandler);
            this.element.removeEventListener(EVENTS.RENDER_COMPLETE, this.onRenderCompleteHandler);
            this.element.removeEventListener(EVENTS.ACTIVATE, this.onActivateEventHandler);
            this.element.removeEventListener(EVENTS.DEACTIVATE, this.onDeactivateEventHandler);
            this.element.removeEventListener(EVENTS.LOG, this.onLogEventHandler);
            this.element.removeEventListener(EVENTS.ERROR, this.onErrorEventHandler);
            this.element.removeEventListener(EVENTS.REACT_ERROR, this.onReactErrorEventHandler);
        }
    }

    /**
     * Возвращает Promise с нодой проинициализированного Реакт-приложения.
     */
    public static onAppReady(element: NodeElement): Promise<NodeElement> {
        return new Promise(function (resolve) {
            if (element?.isAppBlockInited) {
                resolve(element);
            } else {
                const onAppReadyHandler = () => {
                    resolve(element);

                    element?.removeEventListener(EVENTS.READY, onAppReadyHandler);
                };

                element?.addEventListener(EVENTS.READY, onAppReadyHandler);
            }
        });
    }

    public static onAppRenderComplete(element: NodeElement): Promise<NodeElement> {
        return new Promise(function (resolve) {
            if (element?.isAppBlockRenderComplete) {
                resolve(element);
            } else {
                const onAppRenderCompleteHandler = (e: Event) => {
                    const event = e as CustomEvent<{ element: NodeElement }>;
                    if (element?.isEqualNode(event.detail.element)) {
                        resolve(element);
                    }

                    element?.removeEventListener(EVENTS.RENDER_COMPLETE, onAppRenderCompleteHandler);
                };

                element?.addEventListener(EVENTS.RENDER_COMPLETE, onAppRenderCompleteHandler);
            }
        });
    }

    private beforeRender() {
        buildIconCache(this.element!);

        if (this.element) {
            this.element.addEventListener(EVENTS.INIT, this.onInitEventHandler);
            this.element.addEventListener(EVENTS.RENDER_COMPLETE, this.onRenderCompleteHandler);
            this.element.addEventListener(EVENTS.ACTIVATE, this.onActivateEventHandler);
            this.element.addEventListener(EVENTS.DEACTIVATE, this.onDeactivateEventHandler);
            this.element.addEventListener(EVENTS.LOG, this.onLogEventHandler);
            this.element.addEventListener(EVENTS.ERROR, this.onErrorEventHandler);
            this.element.addEventListener(EVENTS.REACT_ERROR, this.onReactErrorEventHandler);
        }
    }

    private render() {
        const element = this.element;

        const ReactAppBlock = this.appModule.tags[this.tagName];
        try {
            ReactAppBlock.render({
                tagName: this.tagName,
                attrs: this.dataAttributes,
                parts: this.uiParts,
                node: element
            });
        } catch (error: unknown) {
            const e = error as Error;
            this.logError(LogEventCode.FIRST_RENDER_ERROR, new ReactCustomError({
                message: e.message,
                stack: e.stack!
            }));
        }
    }

    private logError(code: LogEventCode, payload: Error) {
        OK.Tracer.log(`An error occurred in the "${this.tagName}" component`);

        const reactBlockKey = 'reactBlock';
        OK.Tracer.setErrorKey(reactBlockKey, this.tagName);
        // В Трейсер ошибка попадёт прямо из console.error
        console.error(payload, this.tagName, code);
        OK.Tracer.errorModule?.removeErrorKey(reactBlockKey);

        if (!payload.message.match(IGNORED_ERRORS_REGEXP)) {
            logger.clob('react-error', formatJSONToTable(payload), this.tagName, code);
        }
    }

    private logSuccess(code: LogEventCode, payload?: string) {
        logger.success(code, this.tagName, payload);
    }

    private onInitEventHandler = (e: Event) => {
        const event = e as ReactBlockEvent<{}>;

        if (this.element) {
            Object.assign(this.element, event.detail);
            this.element.isAppBlockInited = true;

            this.element.dispatchEvent(new CustomEvent(EVENTS.READY, {
                bubbles: false,
                cancelable: false
            }));
        }
    }

    private onRenderCompleteHandler = () => {
        this.element!.isAppBlockRenderComplete = true;
    }

    private onActivateEventHandler(e: Event) {
        const event = e as ReactBlockEvent<{element: HTMLElement}>;
        capture.activate(event.detail.element);
    }

    private onDeactivateEventHandler(e: Event) {
        const event = e as ReactBlockEvent<{element: HTMLElement}>;
        const el = event.detail.element;

        capture.deactivateOnlyElement ?
            capture.deactivateOnlyElement(el) :
            capture.deactivate(el);
    }

    private onLogEventHandler(e: Event) {
        const event = e as ReactBlockEvent<{type: string, params: unknown}>;
        /**
         * @type {function}
         */
        const fn = (logger as Record<string, Function>)[event.detail.type];

        fn.apply(logger, event.detail.params || []);
    }

    private onReactErrorEventHandler = (e: Event) => {
        const event = e as ReactBlockEvent<{code: LogEventCode, data: Error}>;
        this.logError(event.detail.code, event.detail.data);
    }

    private onErrorEventHandler = (e: Event) => {
        const event = e as ReactBlockEvent<{}>;
        console.error(this.tagName, event.detail);
        logger.error(this.tagName, event.detail.toString());
    }
}

/**
 * Создаёт кеш для SVG'шных иконок.
 */
function buildIconCache(element: HTMLElement): void {
    const iconWrappers = Array.from(element.getElementsByTagName('span'));

    const lazyIconsCache = window.require('OK/LazyIconsCache');

    iconWrappers.forEach(function (icon) {
        if (icon.hasChildNodes() && icon.firstChild instanceof SVGElement) {
            const iconName = icon.getAttribute('data-icon-name');

            if (iconName) {
                const iconCache = lazyIconsCache.getInstance(iconName);

                if (!iconCache.html) {
                    iconCache.makeNode(icon.innerHTML);
                }
            }
        }
    })
}

function formatJSONToTable(json: object): string {
    return Object.keys(json)
        .map(key => {
            const value = (json as Record<string, unknown>)[key];
            const resVal = typeof value === 'string' ? value : JSON.stringify(value);

            return key + ' = ' + resVal;
        })
        .join('\n');
}

function now(): number {
    return Math.floor(performance.now());
}

function simpleDurationValue(val: number): string {
    if (val < 10) {
        return '0';
    }

    if (val > 5000) {
        return '5000';
    }

    return (Math.floor(val) + '')
        .split('')
        .map((n, i) => i ? '0' : n)
        .join('');
}
